import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("viva_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && localStorage.getItem("viva_token")) {
      localStorage.removeItem("viva_token");
      if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadLedger(format) {
  const res = await api.get("/export/ledger", { params: { format }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = format === "pdf" ? "viva_ledger.pdf" : "viva_ledger.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadRejections() {
  const res = await api.get("/rejections/export", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "viva_rejections_scrap.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadPO(id, poNumber) {
  const res = await api.get(`/purchase-orders/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${poNumber || "purchase-order"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function printPO(id) {
  const res = await api.get(`/purchase-orders/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const w = window.open(url, "_blank");
  if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) { console.error("Print failed", e); } });
}

export async function openUpload(id) {
  const res = await api.get(`/uploads/${id}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
}

export async function openFile(id) {
  const res = await api.get(`/files/${id}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
}

export async function downloadPlan(id, planNumber) {
  const res = await api.get(`/production-plans/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${planNumber || "work-order"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function printPlan(id) {
  const res = await api.get(`/production-plans/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const w = window.open(url, "_blank");
  if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) { console.error("Print failed", e); } });
}

export async function downloadProductionReport(format, month) {
  const params = { format };
  if (month) params.month = month;
  const res = await api.get("/production/report/export", { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  const suffix = month ? `_${month}` : "";
  a.download = format === "pdf" ? `viva_production_report${suffix}.pdf` : `viva_production_report${suffix}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadProductionHistory(format, status, month) {
  const params = { format };
  if (status && status !== "all") params.status = status;
  if (month) params.month = month;
  const res = await api.get("/production/export", { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = format === "pdf" ? "viva_production_history.pdf" : "viva_production_history.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadMaterialsTemplate() {
  const res = await api.get("/materials/import-template", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "viva_materials_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function importMaterials(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/materials/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
}

export async function downloadMaterials(format) {
  const res = await api.get("/materials/export", { params: { format }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = format === "pdf" ? "viva_raw_materials.pdf" : "viva_raw_materials.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadDispatchReport() {
  const res = await api.get("/dispatches/export", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "viva_dispatch_revenue.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default api;
